import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ResmoMascot from "../components/ResmoMascot";
import ProMoInstructions from "../components/ProMoInstructions";
import PasswordInput from "../components/PasswordInput";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useCopy } from "../lib/copy";
import Navbar from "../components/Navbar";
import ServerStatus from "../components/ServerStatus";

export default function Login() {
  const { login, verify2fa } = useAuth();
  const navigate = useNavigate();
  const { isPro } = useTheme();
  const t = useCopy();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [rateLimited, setRateLimited] = useState(null);
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null); // { id, email }
  const [otp, setOtp] = useState("");

  // Honor any 'door' the user picked from the Welcome page Quick-Start picker
  // before they had an account. Stashed in sessionStorage so we don't pollute
  // the URL with redirect params.
  const consumePostAuthNext = () => {
    try {
      const next = sessionStorage.getItem("resmo:postAuthNext");
      if (next) {
        sessionStorage.removeItem("resmo:postAuthNext");
        sessionStorage.removeItem("resmo:postAuthIntent");
        return next;
      }
    } catch { /* sessionStorage blocked */ }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setRateLimited(false);
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.ok) {
      navigate(consumePostAuthNext() || "/");
      return;
    }
    if (result.requires_2fa) {
      setChallenge({ id: result.challenge_id, email: result.email });
      setError("");
      return;
    }
    if (result.status === 429) {
      const minutes = Math.max(1, Math.ceil((result.retryAfter || 900) / 60));
      setRateLimited({
        minutes,
        message: `You've tried too many incorrect passwords. Take a ${minutes}-minute break, or reset your password — we'll be ready when you are.`,
      });
    } else {
      setError("Invalid email or password.");
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!challenge) return;
    setError("");
    setLoading(true);
    const result = await verify2fa(challenge.id, otp);
    setLoading(false);
    if (result.ok) navigate(consumePostAuthNext() || "/");
    else setError(result.detail || "Code didn't match.");
  };

  return (
    <div className="min-h-screen rm-paper">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-12 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
        <div className="hidden md:flex flex-col items-center justify-center">
          {isPro ? (
            <ProMoInstructions
              tag="Sign in"
              title="Pick up where you left off."
              intro="Your tailored resumes, role briefs, and salary assessments are all saved."
              steps={[
                "Enter the email and password you registered with.",
                "Land on your last analysis — or start a new one.",
                "All history syncs across devices.",
              ]}
              footnote="Forgot your password? Use the reset link on this page — we'll email you a fresh one."
              testId="promo-login-instructions"
            />
          ) : (
            <>
              <ResmoMascot state="hungry" size={260} />
              <div className="font-display text-2xl mt-4 uppercase text-center">
                Welcome back.<br />I missed you.
              </div>
            </>
          )}
        </div>

        <div className="rm-card-static p-8 md:p-10">
          <div className="flex items-center mb-3">
            <div className="rm-tag">{challenge ? "Two-factor" : "Login"}</div>
            <ServerStatus />
          </div>
          <h1 className="font-display text-4xl uppercase mb-6">
            {challenge ? "One more step." : t("Get back to feeding.", "Sign in.")}
          </h1>

          {challenge ? (
            <form onSubmit={handleVerifyOtp} className="space-y-5" data-testid="login-2fa-form">
              <p className="text-sm" style={{ color: "#475569" }}>
                Open your authenticator app and enter the 6-digit code for
                <b style={{ color: "#0F1B2D" }}> {challenge.email}</b>. (You can also use a recovery code.)
              </p>
              <input
                type="text"
                inputMode="numeric"
                autoFocus
                required
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/[^0-9a-f-]/gi, "").slice(0, 32))}
                placeholder="123 456"
                className="rm-input text-center text-2xl tracking-[0.4em]"
                data-testid="login-2fa-input"
              />
              {error && (
                <div className="font-mono-rm text-sm p-3 rounded-md" style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }} data-testid="login-2fa-error">
                  {error}
                </div>
              )}
              <button type="submit" disabled={loading || !otp} className="rm-btn rm-btn-primary w-full disabled:opacity-50" data-testid="login-2fa-submit-btn">
                {loading ? "Verifying…" : "Verify & sign in"}
              </button>
              <button type="button" onClick={() => { setChallenge(null); setOtp(""); setError(""); }} className="text-xs underline" style={{ color: "#64748B" }}>
                ← Back to sign in
              </button>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="rm-label block mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rm-input"
                data-testid="login-email-input"
                placeholder="you@email.com"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="rm-label">Password</label>
                <Link to="/forgot-password" className="text-xs font-mono-rm underline"
                      style={{ color: "#FF6A00", fontWeight: 700 }}
                      data-testid="login-forgot-link">
                  Forgot password?
                </Link>
              </div>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                minLength={1}
                autoComplete="current-password"
                testId="login-password-input"
                ariaLabel="Password"
              />
            </div>
            {rateLimited && (
              <div
                className="rounded-md p-4 flex items-start gap-3"
                style={{
                  background: "linear-gradient(135deg, #FFF4E0 0%, #FFE6C7 100%)",
                  border: "3px solid #B91C1C",
                  boxShadow: "4px 4px 0 0 #0F1B2D",
                }}
                data-testid="login-rate-limit"
              >
                <div className="font-display text-2xl flex-shrink-0" style={{ color: "#B91C1C" }}>⏳</div>
                <div className="flex-1 min-w-0">
                  <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-1"
                       style={{ color: "#B91C1C", fontWeight: 700 }}>
                    Slow down · {rateLimited.minutes}-min cooldown
                  </div>
                  <p className="text-sm leading-snug" style={{ color: "#0F1B2D" }}>
                    {rateLimited.message}
                  </p>
                  <Link
                    to="/forgot-password"
                    className="rm-btn rm-btn-primary text-xs mt-2.5 inline-flex"
                    data-testid="login-reset-cta"
                  >
                    Reset my password →
                  </Link>
                </div>
              </div>
            )}
            {error && !rateLimited && (
              <div className="font-mono-rm text-sm p-3 rounded-md" style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }} data-testid="login-error">
                {error}
              </div>
            )}
            <button type="submit" disabled={loading} className="rm-btn rm-btn-primary w-full" data-testid="login-submit-btn">
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>
          )}
          <div className="mt-6 text-sm font-mono-rm">
            New here?{" "}
            <Link to="/register" className="font-bold underline" data-testid="login-to-register-link">
              Create an account
            </Link>
          </div>
          {/* Cross-link to the employer portal — a lost recruiter shouldn't
              have to guess the URL. Kept small so it never distracts candidates. */}
          <div className="mt-3 pt-3 text-xs font-mono-rm uppercase tracking-widest"
               style={{ borderTop: "1px dashed #E2E8F0", color: "#94A3B8" }}>
            · Are you an employer?{" "}
            <Link to="/employer/login" className="font-bold" style={{ color: "#FF6A00" }}
                  data-testid="login-employer-link">
              Sign in to the Talent Directory →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
